// Accès indirect aux corps d'articles, pour les garder HORS du bundle initial.
//
// `articleContent.ts` agrège ~1 Mo de HTML (32 articles × 3 locales). Tant que
// `BlogArticlePage` l'importait statiquement, il faisait partie du graphe de
// `App.tsx` : Vite émettait un `<link rel="modulepreload">` sur *chaque* page et
// le navigateur téléchargeait 300 Ko gzip d'articles sur la home, en priorité
// haute, en concurrence avec le CSS et le JS applicatif.
//
// Ce module est volontairement léger (aucun import statique du contenu) :
//  - au prérendu, `entry-server` injecte l'accesseur synchrone — indispensable,
//    `renderToString` ne peut rien attendre et le corps doit être dans le HTML ;
//  - côté client, le chunk n'est chargé que sur une route article.
import { type Locale } from '../i18n'

type ArticleHtmlGetter = (locale: Locale, id: string) => string | undefined

let getter: ArticleHtmlGetter | null = null
let pending: Promise<void> | null = null

/** Injecté par entry-server : le prérendu résout les corps de façon synchrone. */
export function provideArticleContent(fn: ArticleHtmlGetter) {
  getter = fn
}

/** true dès que les corps sont résolvables sans attente (SSR, ou chunk chargé). */
export function isArticleContentReady() {
  return getter !== null
}

/** Corps de l'article, ou undefined tant que le chunk n'est pas chargé. */
export function readArticleHtml(locale: Locale, id: string) {
  return getter?.(locale, id)
}

/** Charge le chunk d'articles à la demande (idempotent, dédupliqué). */
export function loadArticleContent(): Promise<void> {
  if (getter) return Promise.resolve()
  pending ??= import('./articleContent').then((m) => provideArticleContent(m.getArticleHtml))
  return pending
}
